import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiCheck, FiStar, FiCreditCard, FiDownload, FiX } = FiIcons

const Plan = () => {
  const { user, profile, refreshProfile } = useAuth()
  const [currentPlan, setCurrentPlan] = useState('free')
  const [loading, setLoading] = useState(false)
  const [showProModal, setShowProModal] = useState(false)

  // Load current plan from user profile
  useEffect(() => {
    if (profile) {
      // Check if user is beta user or has pro role
      if (profile.beta_user || profile.role === 'pro') {
        setCurrentPlan('pro')
      } else {
        setCurrentPlan('free')
      }
    }
  }, [profile])

  const plans = {
    free: {
      name: 'Free',
      price: '$0',
      period: 'forever',
      features: [
        'All core editing features',
        '2 AI project generations per month',
        '20 AI image generations per month',
        'PDF export',
        'Up to 10 projects',
        'NuPitch badge on shared decks',
        'Basic templates'
      ]
    },
    pro: {
      name: 'Pro',
      price: '$19',
      period: 'per month',
      features: [
        'Everything in Free',
        '20 AI project generations per month',
        '200 AI image generations per month',
        'Unlimited projects',
        'Remove NuPitch badge',
        'Pro badge on public profile',
        'Priority AI generation',
        'Advanced templates',
        'Custom branding options',
        'Analytics dashboard',
        'HD image exports',
        'Email support'
      ]
    }
  }

  const handleUpgrade = () => {
    setShowProModal(true)
  }

  const confirmUpgrade = async () => {
    try {
      setLoading(true)
      const { supabase } = await import('../lib/supabase')
      const creditService = (await import('../lib/creditService')).default
      
      // Update user to beta user (gives Pro access during beta)
      const { error } = await supabase
        .from('users')
        .update({ 
          beta_user: true,
          updated_at: new Date()
        })
        .eq('id', user.id)

      if (error) throw error

      // Force quota renewal to give Pro credits immediately
      await creditService.forceQuotaRenewal(user.id)

      // Refresh profile to get updated beta_user status
      await refreshProfile()
      
      setCurrentPlan('pro')
      setShowProModal(false)
      alert('Welcome to NuPitch Pro! 🎉\n\nYour credits have been refreshed to Pro limits.')
    } catch (err) {
      console.error('Error upgrading to pro:', err)
      alert('Error upgrading to Pro. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDowngrade = async () => {
    if (confirm('Are you sure you want to downgrade to Free? You\'ll lose access to Pro features.')) {
      try {
        const { supabase } = await import('../lib/supabase')
        const creditService = (await import('../lib/creditService')).default
        
        // Update user to remove beta status
        const { error } = await supabase
          .from('users')
          .update({ 
            beta_user: false,
            updated_at: new Date()
          })
          .eq('id', user.id)

        if (error) throw error

        // Force quota renewal to Free credits
        await creditService.forceQuotaRenewal(user.id)

        // Refresh profile to get updated beta_user status
        await refreshProfile()
        
        setCurrentPlan('free')
        alert('You\'ve been downgraded to the Free plan.')
      } catch (err) {
        console.error('Error downgrading:', err)
        alert('Error downgrading. Please try again.')
      }
    }
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Beta Pro Modal */}
      {showProModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 relative">
            <button
              onClick={() => setShowProModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg"
            >
              <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiStar} className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Beta!</h3>
              <p className="text-gray-600">
                While NuPitch is in beta, everyone has access to <strong>Pro features for free</strong>! 
                Help us improve by providing feedback.
              </p>
            </div>

            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 text-primary-700 mb-2">
                <SafeIcon icon={FiCheck} className="w-5 h-5" />
                <span className="font-medium">What you get:</span>
              </div>
              <ul className="text-sm text-primary-600 space-y-1 ml-7">
                <li>• 20 AI project generations/month</li>
                <li>• 200 AI image generations/month</li>
                <li>• Unlimited projects</li>

                <li>• Custom branding</li>
                <li>• Email support</li>
              </ul>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowProModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Maybe Later
              </button>
              <button
                onClick={confirmUpgrade}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <SafeIcon icon={FiStar} className="w-4 h-4" />
                    <span>Activate Pro</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
        <p className="text-xl text-gray-600">Unlock the full potential of NuPitch</p>
        {currentPlan === 'pro' && (
          <div className="mt-4 inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full">
            <SafeIcon icon={FiStar} className="w-4 h-4" />
            <span className="font-medium">Beta Pro Access Active</span>
          </div>
        )}
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        {Object.entries(plans).map(([planKey, plan]) => (
          <div
            key={planKey}
            className={`relative rounded-2xl border-2 p-8 ${
              planKey === 'pro' 
                ? 'border-primary-500 bg-primary-50 shadow-lg' 
                : 'border-gray-200 bg-white'
            }`}
          >
            {planKey === 'pro' && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-1">
                  <SafeIcon icon={FiStar} className="w-4 h-4" />
                  <span>Most Popular</span>
                </span>
              </div>
            )}

            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
              <div className="flex items-baseline justify-center space-x-1">
                <span className={`text-4xl font-bold ${planKey === 'pro' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                  {plan.price}
                </span>
                {planKey === 'pro' && (
                  <span className="text-4xl font-bold text-primary-600">FREE</span>
                )}
                <span className="text-gray-600">/{plan.period}</span>
              </div>
              {planKey === 'pro' && (
                <p className="text-sm text-primary-600 mt-2 font-medium">During Beta</p>
              )}
            </div>

            <ul className="space-y-4 mb-8">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <SafeIcon 
                    icon={FiCheck} 
                    className={`w-5 h-5 mt-0.5 ${
                      planKey === 'pro' ? 'text-primary-600' : 'text-green-600'
                    }`} 
                  />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="text-center">
              {currentPlan === planKey ? (
                <div className="space-y-2">
                  <div className="bg-gray-100 text-gray-600 py-3 px-6 rounded-lg font-medium">
                    Current Plan
                  </div>
                  {planKey === 'pro' && (
                    <button
                      onClick={handleDowngrade}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Downgrade to Free
                    </button>
                  )}
                </div>
              ) : planKey === 'pro' ? (
                <button
                  onClick={handleUpgrade}
                  className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiStar} className="w-5 h-5" />
                  <span>Get Pro (Free in Beta)</span>
                </button>
              ) : planKey === 'free' && currentPlan === 'pro' ? (
                <button
                  onClick={handleDowngrade}
                  className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                >
                  Downgrade to Free
                </button>
              ) : (
                <div className="bg-gray-100 text-gray-600 py-3 px-6 rounded-lg font-medium">
                  Current Plan
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Current Plan Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Current Plan</h3>
            <p className="text-gray-600">
              You are currently on the <strong>{plans[currentPlan].name}</strong> plan
            </p>
          </div>
          {currentPlan === 'pro' && (
            <div className="flex items-center space-x-2 text-primary-600">
              <SafeIcon icon={FiStar} className="w-5 h-5" />
              <span className="font-medium">Pro Member</span>
            </div>
          )}
        </div>

        {currentPlan === 'pro' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  <strong>Beta Access:</strong> Free Pro features during development
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  You'll be notified before any billing begins
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FAQ */}
      <div className="mt-12">
        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h3>
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-2">What happens when beta ends?</h4>
            <p className="text-gray-600">
              We'll notify all beta users at least 30 days before transitioning to paid plans. 
              You'll have the option to continue with Pro or switch to Free.
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-2">Can I download my presentations?</h4>
            <p className="text-gray-600">
              Pro users can export high-quality images and access advanced download features. 
              Free users can export to PDF.
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-2">What happens to my projects if I downgrade?</h4>
            <p className="text-gray-600">
              All your projects remain accessible. However, if you have more than 10 projects, 
              you'll need to delete some to create new ones on the Free plan.
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-2">⚠️ Important: Beta Data Safety</h4>
            <p className="text-gray-600">
              <strong>Please download your slide decks as PDF regularly.</strong> While we're in beta, 
              significant platform changes may occasionally occur. We do everything possible to save 
              and backup your projects, but downloading your work as PDF ensures you always have access 
              to your presentations.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Plan